
import { useState, useEffect, useCallback, useMemo } from 'react';
import { UserProfile, MatchType } from '../../types';
import { ArenaResult, GhostDuelResult } from '../../arena/types';

const getStorageItem = (key: string, defaultValue: any) => {
    if (typeof window === 'undefined') return defaultValue;
    const saved = localStorage.getItem(key);
    if (saved === null) return defaultValue;
    try { return JSON.parse(saved); } catch { return saved; }
};

const DEFAULT_PROFILE: UserProfile = {
    id: "local_user_01",
    pseudo: "Bedroom Hero",
    avatarUrl: "",
    title: "ROOKIE",
    level: 1,
    xp: 0,
    streak: 0,
    badges: [],
    // NO-SIM: Initialisation à 0 pour prouver le calcul ultérieur
    stats: {
        avgScore: 0, totalMatches: 0, wins: 0, bestRank: "N/A", levelXP: 0,
        timingSkill: 0, soulSkill: 0, cleanSkill: 0
    },
    favoriteBands: [], favoriteGuitarists: [], favoriteAlbums: [], favoriteSongs: []
};

export function useGamification() {
    // Persistent State
    const [userProfile, setUserProfile] = useState<UserProfile>(() => getStorageItem('gsc_user_profile', DEFAULT_PROFILE));
    const [analysisCount, setAnalysisCount] = useState<number>(() => getStorageItem('gsc_analysis_count', 0));
    const [leagueChallengeId, setLeagueChallengeId] = useState<string | null>(() => getStorageItem('gsc_league_challenge_id', null));

    // Ephemeral State
    const [matchType, setMatchType] = useState<MatchType>('FILE');
    const [matchSourceName, setMatchSourceName] = useState<string>("Master");
    const [arenaTargetScore, setArenaTargetScore] = useState<number>(0);
    const [arenaResult, setArenaResult] = useState<ArenaResult | null>(null);
    const [arenaDuelResult, setArenaDuelResult] = useState<GhostDuelResult | null>(null);
    // Fix (Axe 5 - Security): isPremium cannot be controlled by localStorage locally for security reasons.
    // It should be driven by a backend or an ephemeral token. Default to false.
    const [isPremium, setIsPremium] = useState(false);

    // Persistence Effects
    useEffect(() => { localStorage.setItem('gsc_analysis_count', JSON.stringify(analysisCount)); }, [analysisCount]);
    useEffect(() => { localStorage.setItem('gsc_user_profile', JSON.stringify(userProfile)); }, [userProfile]);
    useEffect(() => { localStorage.setItem('gsc_league_challenge_id', JSON.stringify(leagueChallengeId)); }, [leagueChallengeId]);
    // Removed persistence for isPremium from local storage for security (Axe 5)
    // useEffect(() => { localStorage.setItem('gsc_is_premium', JSON.stringify(isPremium)); }, [isPremium]);

    const incrementAnalysisCount = useCallback(() => setAnalysisCount(prev => prev + 1), []);
    const updateUserProfile = useCallback((patch: Partial<UserProfile>) => setUserProfile(prev => ({ ...prev, ...patch })), []);

    return useMemo(() => ({
        userProfile, updateUserProfile,
        analysisCount, incrementAnalysisCount,
        leagueChallengeId, setLeagueChallengeId,
        matchType, setMatchType,
        matchSourceName, setMatchSourceName,
        arenaTargetScore, setArenaTargetScore,
        arenaResult, setArenaResult,
        arenaDuelResult, setArenaDuelResult,
        isPremium, setIsPremium
    }), [
        userProfile, analysisCount, leagueChallengeId, matchType, matchSourceName,
        arenaTargetScore, arenaResult, arenaDuelResult, isPremium,
        updateUserProfile, incrementAnalysisCount
    ]);
}
